import { useId, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import { fonts } from 'src/theme';
import ltaLogo from 'src/assets/images/lta-logo.png';
import { highlight, neutralDark, neutralLight } from 'src/theme/palette';

export type MobileNavBarVariant = 'page' | 'logo' | 'dashboard';

export type MobileNavRightControl = 'none' | 'icon' | 'text';

export interface MobileNavBarProps {
  variant?: MobileNavBarVariant;
  title?: string;
  rightControl?: MobileNavRightControl;
  rightText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onBack?: () => void;
  onRightClick?: () => void;
  menuOpen?: boolean;
  menuId?: string;
  userInitial?: string;
  userName?: string;
  avatarColour?: string;
  onAvatarClick?: () => void;
}

function RightControl({
  rightControl,
  rightText,
  rightIcon,
  onRightClick,
}: Pick<MobileNavBarProps, 'rightControl' | 'rightText' | 'rightIcon' | 'onRightClick'>) {
  if (rightControl === 'icon') {
    return (
      <IconButton
        size="small"
        onClick={onRightClick}
        aria-label="Action"
        sx={{ color: highlight.darkest }}
      >
        {rightIcon ?? <CloseIcon sx={{ fontSize: 20 }} />}
      </IconButton>
    );
  }

  if (rightControl === 'text') {
    return (
      <Typography
        component="button"
        onClick={onRightClick}
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: highlight.darkest,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          p: 0,
        }}
      >
        {rightText ?? 'Sign in'}
      </Typography>
    );
  }

  return null;
}

export function MobileNavBar({
  variant = 'page',
  title,
  rightControl = 'none',
  rightText,
  leftIcon,
  rightIcon,
  onBack,
  onRightClick,
  menuOpen,
  menuId,
  userInitial,
  userName,
  avatarColour,
  onAvatarClick,
}: MobileNavBarProps) {
  const titleId = useId();

  if (variant === 'logo') {
    return (
      <Box
        component="header"
        aria-labelledby={title ? titleId : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        {leftIcon ?? (
          <Box component="img" src={ltaLogo} alt="LTA Activators" width={53} height={32} />
        )}

        {title && (
          <Typography
            id={titleId}
            variant="h4"
            component="h1"
            sx={{
              fontFamily: fonts.body,
              letterSpacing: 0,
            }}
          >
            {title}
          </Typography>
        )}

        <RightControl
          rightControl={rightControl}
          rightText={rightText}
          rightIcon={rightIcon}
          onRightClick={onRightClick}
        />
      </Box>
    );
  }

  if (variant === 'dashboard') {
    return (
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          height: 56,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component={onAvatarClick ? 'button' : 'div'}
          onClick={onAvatarClick}
          aria-label={onAvatarClick ? 'Change profile colour' : undefined}
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: avatarColour ?? highlight.darkest,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: 'none',
            p: 0,
            cursor: onAvatarClick ? 'pointer' : 'default',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: highlight.darkest,
              outlineOffset: 4,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              fontFamily: fonts.body,
              color: neutralLight.lightest,
              lineHeight: 1,
            }}
          >
            {userInitial ?? '?'}
          </Typography>
        </Box>

        <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500, color: neutralDark.darkest }}>
          Welcome {userName}
        </Typography>

        <IconButton
          onClick={onRightClick}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          aria-haspopup="dialog"
          sx={{ color: highlight.darkest, mr: -1 }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      component="header"
      aria-labelledby={title ? titleId : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        // Grows past 56px when a long title wraps onto more lines.
        minHeight: 56,
        bgcolor: 'background.paper',
      }}
    >
      <IconButton
        edge="start"
        onClick={onBack}
        aria-label="Go back"
        sx={{ color: highlight.darkest, ml: 1, flexShrink: 0 }}
      >
        {leftIcon ?? <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />}
      </IconButton>

      {title && (
        <Typography
          id={titleId}
          variant="h6"
          component="h1"
          sx={{
            // Side slots are equal widths, so the centred text stays centred
            // on the header rather than on the space left between them.
            flex: 1,
            minWidth: 0,
            textAlign: 'center',
            py: 1,
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ ml: 'auto', mr: 1, flexShrink: 0 }}>
        {rightControl === 'none' ? (
          <Box sx={{ width: 40, height: 40 }} />
        ) : (
          <RightControl
            rightControl={rightControl}
            rightText={rightText}
            rightIcon={rightIcon}
            onRightClick={onRightClick}
          />
        )}
      </Box>
    </Box>
  );
}
